import { rest } from '@ax2/common/index';
import SurveySchema from '@ax2/common/model/schemas/SurveySchema';
import {
  getExternalId,
  getExternalSurveyId,
  getInternalSurveyId,
  getSurveyToken,
} from '../helpers/surveyData';
import AnswerData from '@ax2/common/model/answers-data/AnswerData';
import Feedback from '@ax2/common/model/Feedback';

function withParams(path: string, params: { [key: string]: string }) {
  const searchParams = new URLSearchParams();
  for (const key in params) {
    searchParams.set(key, params[key]);
  }
  const search = searchParams.toString();
  return search.length > 0 ? `${path}?${search}` : path;
}

function withSurveyData(path: string): string {
  const token = getSurveyToken();
  const externalId = getExternalId();
  const internalSurveyId = getInternalSurveyId();
  const externalSurveyId = getExternalSurveyId();
  if (token) {
    return withParams(path, { token });
  } else if (externalId && externalSurveyId && internalSurveyId) {
    return withParams(path, {
      eid: externalId,
      isid: internalSurveyId,
      esid: externalSurveyId,
    });
  } else {
    return path;
  }
}

function withSurveyDataAndParams(
  path: string,
  additionalParams: { [key: string]: string }
): string {
  const token = getSurveyToken();
  const externalId = getExternalId();
  const internalSurveyId = getInternalSurveyId();
  const externalSurveyId = getExternalSurveyId();
  const tokenParam: { [key: string]: string } = token ? { token: token } : {};
  const externalIdParam: { [key: string]: string } = externalId
    ? { eid: externalId }
    : {};
  const internalSurveyIdParam: { [key: string]: string } = internalSurveyId
    ? { isid: internalSurveyId }
    : {};
  const externalSurveyIdParam: { [key: string]: string } = externalSurveyId
    ? { esid: externalSurveyId }
    : {};
  const params = {
    ...{},
    ...tokenParam,
    ...externalIdParam,
    ...internalSurveyIdParam,
    ...externalSurveyIdParam,
    ...additionalParams,
  };
  return withParams(path, params);
}

export function getSurvey(): Promise<SurveySchema> {
  return rest.api<SurveySchema>(withSurveyData('/takesurvey/v1/survey'), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=UTF-8',
    },
  });
}

export function getAnswers(): Promise<AnswerData[]> {
  const url = '/takesurvey/v1/survey/answers';
  return rest.api<AnswerData[]>(withSurveyData(url), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=UTF-8',
    },
  });
}

export function sendAnswer(data: AnswerData): Promise<void> {
  const url = '/takesurvey/v1/answer';
  return rest.api<void>(
    withSurveyDataAndParams(url, { questionId: data.questionId }),
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=UTF-8',
      },
      data: JSON.stringify(data),
    }
  );
}

export function completeSurvey(): Promise<void> {
  const url = '/takesurvey/v1/survey/complete';
  return rest.api<void>(withSurveyData(url), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=UTF-8',
    },
  });
}

export function screenedOutSurvey(): Promise<void> {
  const url = '/takesurvey/v1/screened-out';
  return rest.api<void>(withSurveyData(url), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=UTF-8',
    },
  });
}

export function sendFeedback(data: Feedback): Promise<void> {
  const url = '/takesurvey/v1/feedback';
  return rest.api<void>(withSurveyData(url), {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json;charset=UTF-8',
    },
    data: JSON.stringify(data),
  });
}

export function fetchImage(key: string): Promise<Blob> {
  const currentUrl = window.location.pathname;
  let basicUrl = 'takesurvey';
  if (currentUrl.includes('/surveyBuilder/')) basicUrl = 'makesurvey';
  return rest
    .apiResponse(`/${basicUrl}/v1/image/${key}`, {
      method: 'GET',
      responseType: 'blob',
    })
    .then(response => {
      return new Blob([response.data], {
        type: 'image/*',
      });
    });
}
